/** One label/value row inside a detail section. */
export interface DetailSectionItem {
  label: string;
  value?: unknown;
  /**
   * Machine key. `id`, `*Id`, `slug`, `*Slug`, and `code` are not rendered
   * unless `project` is set. Business keys such as `sapCode` stay visible.
   */
  key?: string;
  /** Show this row even when `key` is an internal code or slug. */
  project?: boolean;
  /** Long text spans the full field grid. */
  wide?: boolean;
}

const NULLISH_WORD = /^(null|undefined)$/i;

export function isBlankDetailValue(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'number' && Number.isNaN(value)) return true;
  const text = String(value).replace(/\u00a0/g, ' ').trim();
  return text.length === 0 || NULLISH_WORD.test(text);
}

/** Empty, null, and the words null/undefined become an em dash. */
export function formatDetailValue(value: unknown): string {
  if (isBlankDetailValue(value)) return '—';
  return String(value).replace(/\u00a0/g, ' ').trim();
}

/** Internal identifiers are not their own row unless the consumer projects them. */
export function isInternalDetailKey(key: string | null | undefined): boolean {
  if (!key?.trim()) return false;
  const trimmed = key.trim();
  if (/^(id|uuid|slug|code)$/i.test(trimmed)) return true;
  if (/(Id|_id)$/.test(trimmed)) return true;
  if (/(Slug|_slug)$/i.test(trimmed)) return true;
  return false;
}

export function projectDetailItems<T extends { key?: string; project?: boolean }>(
  items: readonly T[] | null | undefined,
): T[] {
  if (!items?.length) return [];
  return items.filter((item) => item.project === true || !isInternalDetailKey(item.key));
}
